/**
 * GW-6428: Sentry fingerprinting helpers.
 *
 * Without an explicit fingerprint, Sentry groups every error from
 * BaseApiClient.throwApiError into a single issue (because they share a stack
 * trace). That collapses 401/403/404/500 across every endpoint into one group
 * titled by whichever message arrived last. This module produces stable
 * fingerprints from the underlying ApiError so each (status, route) pair lands
 * in its own Sentry issue.
 *
 * GW-6533: Pre-flight `InvalidOrgIdError` regressions are fingerprinted with
 * a `frontend-bug` prefix so they land in a dedicated alert lane, distinct
 * from network 4xx/5xx noise (`api-error` prefix). This way, when a missing
 * orgId regression ships, the failure shows up in monitoring as a stack-trace
 * pinned bug rather than a wave of opaque API errors.
 */

import { ApiError } from '@/api/apiError';
import { InvalidOrgIdError } from '@/types/orgId';

/**
 * Strip volatile path segments so a URL like
 *   /v1/organizations/abc-123/tokens/balance
 * becomes
 *   /v1/organizations/:id/tokens/balance
 *
 * We replace UUIDs, ULIDs, and any 12+ character hex/alphanumeric segments with
 * `:id`. Numeric-only segments are also replaced. Query strings are dropped.
 */
export function normalizeUrlForFingerprint(rawUrl: string): string {
  let url: URL | null = null;
  try {
    url = new URL(rawUrl, 'http://placeholder.invalid');
  } catch {
    return rawUrl;
  }
  const pathname = url.pathname;
  const segments = pathname.split('/').map((segment) => {
    if (!segment) return segment;
    if (
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(segment)
    ) {
      return ':id';
    }
    if (/^[0-9A-HJKMNP-TV-Z]{26}$/.test(segment)) {
      return ':id';
    }
    if (/^\d+$/.test(segment)) {
      return ':id';
    }
    if (
      segment.length >= 16 &&
      /[a-zA-Z]/.test(segment) &&
      /\d/.test(segment) &&
      /^[0-9a-zA-Z_-]+$/.test(segment)
    ) {
      return ':id';
    }
    if (segment === 'undefined' || segment === 'null') {
      return ':invalid';
    }
    return segment;
  });
  return segments.join('/') || '/';
}

/**
 * Build the fingerprint array for an ApiError. Returning `null` means
 * "fall through to Sentry's default fingerprinting".
 */
export function fingerprintForApiError(error: ApiError): string[] | null {
  const status = typeof error.status === 'number' ? String(error.status) : 'no-status';

  let route: string | null = null;
  const data = error.data as { __requestUrl?: unknown; url?: unknown; path?: unknown } | undefined;
  if (typeof data?.__requestUrl === 'string') route = normalizeUrlForFingerprint(data.__requestUrl);
  else if (typeof data?.url === 'string') route = normalizeUrlForFingerprint(data.url);
  else if (typeof data?.path === 'string') route = normalizeUrlForFingerprint(data.path);

  if (route) {
    return ['api-error', status, route];
  }
  return ['api-error', status];
}

/**
 * GW-6533: Build the fingerprint array for an `InvalidOrgIdError`. Returning
 * a `frontend-bug` prefix steers these failures into a dedicated Sentry alert
 * lane, separate from `api-error` 4xx/5xx noise.
 */
export function fingerprintForInvalidOrgIdError(error: InvalidOrgIdError): string[] {
  // The `received` field is a truncated copy of the input that triggered the
  // assertion. Including it in the fingerprint keeps the grouping stable
  // across deploys without leaking PII (orgIds are public identifiers and
  // `received` is truncated to 64 chars).
  return ['frontend-bug', 'invalid-orgid', error.received];
}

/**
 * Inspect an unknown thrown value and produce a fingerprint when it is an
 * ApiError or InvalidOrgIdError. Anything else returns `null` so Sentry falls
 * back to default grouping.
 */
export function fingerprintForThrown(value: unknown): string[] | null {
  if (value instanceof InvalidOrgIdError) return fingerprintForInvalidOrgIdError(value);
  // GW-6533: Recognize prototype-stripped InvalidOrgIdError-shaped objects
  // (e.g. when re-thrown across a structured-clone boundary).
  if (
    value &&
    typeof value === 'object' &&
    'name' in value &&
    (value as { name?: unknown }).name === 'InvalidOrgIdError'
  ) {
    const maybe = value as { received?: unknown };
    const received = typeof maybe.received === 'string' ? maybe.received : 'unknown';
    return ['frontend-bug', 'invalid-orgid', received];
  }
  if (value instanceof ApiError) return fingerprintForApiError(value);
  if (
    value &&
    typeof value === 'object' &&
    'name' in value &&
    (value as { name?: unknown }).name === 'ApiError'
  ) {
    const maybe = value as { status?: number; data?: unknown };
    const synthetic = new ApiError(
      'message' in value ? String((value as { message?: unknown }).message ?? '') : '',
      typeof maybe.status === 'number' ? maybe.status : undefined,
      maybe.data
    );
    return fingerprintForApiError(synthetic);
  }
  return null;
}
